import { useState, useEffect } from "react";
import { api } from "../App";
import { styledToast as toast } from "@/components/ui/sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { motion } from "framer-motion";
import { Crown, Plus, Users, Mail, Phone, Shield, UserCheck, UserX, Edit, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { useTranslation } from 'react-i18next';
import { LoadingOverlay } from "./LoadingOverlay";

// GOK designation options
const GOK_DESIGNATIONS = [
  { value: "ac", labelKey: "gok.ac" },
  { value: "dc", labelKey: "gok.dc" },
  { value: "gok_secretary", labelKey: "gok.secretary" },
  { value: "principal_secretary", labelKey: "gok.principalSecretary" },
  { value: "minister", labelKey: "gok.minister" },
];

export default function GOKOfficialsManagement({ user }) {
  const { t } = useTranslation();
  const [gokOfficials, setGokOfficials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    gok_designation: "",
  });

  useEffect(() => {
    loadGOKOfficials();
  }, []);

  const loadGOKOfficials = async () => {
    try {
      // Fetch officials with GOK department or is_gok_official flag
      const response = await api.get("/officials?department=gok");
      setGokOfficials(response.data);
    } catch (error) {
      toast.error(t('common.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGOKOfficial = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.password || !formData.gok_designation) {
      toast.error(t('gok.fillAllFields'));
      return;
    }

    setCreateLoading(true);
    try {
      await api.post("/officials/gok", formData);
      toast.success(t('gok.officialCreated'));
      setShowCreateDialog(false);
      setFormData({ name: "", email: "", phone: "", password: "", gok_designation: "" });
      loadGOKOfficials();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('gok.errorCreating'));
    } finally {
      setCreateLoading(false);
    }
  };

  const handleToggleStatus = async (officialId, isDisabled) => {
    try {
      await api.put(`/officials/${officialId}/toggle-status`);
      toast.success(isDisabled ? t('officialsManagement.officialEnabled') : t('officialsManagement.officialDisabled'));
      loadGOKOfficials();
    } catch (error) {
      toast.error(t('officialsManagement.errorToggling'));
    }
  };

  const getDesignationLabel = (designation) => {
    const found = GOK_DESIGNATIONS.find(d => d.value === designation);
    return found ? t(found.labelKey) : designation;
  };

  if (loading) {
    return <LoadingOverlay fullPage={false} />;
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="p-6 bg-gradient-to-r from-amber-50 to-amber-100/50 border border-amber-200">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-amber-600 rounded-xl">
              <Crown className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-manrope font-bold text-amber-800">{t('gok.gokManagement')}</h2>
              <p className="text-sm text-amber-600">{t('gok.gokManagementDesc')}</p>
            </div>
          </div>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-amber-700 hover:bg-amber-800 text-white"
            data-testid="create-gok-official-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('gok.createGOKOfficial')}
          </Button>
        </div>
      </Card>

      {/* GOK Officials List */}
      <div className="grid gap-4">
        {gokOfficials.length === 0 ? (
          <Card className="p-8 text-center">
            <Crown className="w-12 h-12 mx-auto text-amber-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">{t('gok.noGOKOfficials')}</h3>
            <p className="text-sm text-slate-500">{t('gok.noGOKOfficialsDesc')}</p>
          </Card>
        ) : (
          gokOfficials.map((official, index) => (
            <motion.div
              key={official.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card 
                className={`p-5 border ${official.is_disabled ? 'bg-slate-50 border-slate-200' : 'bg-white border-amber-200'}`}
                data-testid={`gok-official-card-${official.id}`}
              >
                <div className="flex flex-col sm:flex-row justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-xl ${official.is_disabled ? 'bg-slate-200' : 'bg-amber-100'}`}>
                      <Crown className={`w-5 h-5 ${official.is_disabled ? 'text-slate-500' : 'text-amber-700'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className={`font-semibold ${official.is_disabled ? 'text-slate-500' : 'text-slate-900'}`}>
                          {official.name}
                        </h3>
                        {official.is_disabled && (
                          <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-300">
                            {t('officialsManagement.disabled')}
                          </Badge>
                        )}
                      </div>
                      <Badge className="mt-1 bg-amber-100 text-amber-800 border-amber-200">
                        {getDesignationLabel(official.designation)}
                      </Badge>
                      
                      <div className="mt-3 space-y-1 text-sm text-slate-600">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-slate-400" />
                          <span>{official.email}</span>
                        </div>
                        {official.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-slate-400" />
                            <span>{official.phone}</span>
                          </div>
                        )}
                        {official.created_at && (
                          <div className="flex items-center gap-2 text-xs text-slate-400">
                            <span>{t('gok.createdOn')}: {format(new Date(official.created_at), "MMM dd, yyyy")}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleStatus(official.id, official.is_disabled)}
                      className={official.is_disabled 
                        ? "text-emerald-600 border-emerald-300 hover:bg-emerald-50" 
                        : "text-amber-600 border-amber-300 hover:bg-amber-50"
                      }
                      data-testid={`toggle-gok-official-${official.id}`}
                    >
                      {official.is_disabled ? (
                        <>
                          <UserCheck className="w-4 h-4 mr-1" />
                          {t('officialsManagement.enable')}
                        </>
                      ) : (
                        <>
                          <UserX className="w-4 h-4 mr-1" />
                          {t('officialsManagement.disable')}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))
        )}
      </div>

      {/* Create GOK Official Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-600" />
              {t('gok.createGOKOfficial')}
            </DialogTitle>
            <DialogDescription>
              {t('gok.createGOKOfficialDesc')}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateGOKOfficial} className="space-y-4 py-4">
            <div>
              <Label htmlFor="gok-name">{t('officialsManagement.fullName')}</Label>
              <Input
                id="gok-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('officialsManagement.fullName')}
                className="mt-1"
                data-testid="gok-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="gok-email">{t('officialsManagement.email')}</Label>
              <Input
                id="gok-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="example@gok.in"
                className="mt-1"
                data-testid="gok-email-input"
              />
            </div>
            
            <div>
              <Label htmlFor="gok-phone">{t('officialsManagement.phone')}</Label>
              <Input
                id="gok-phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="10-digit phone number"
                className="mt-1"
                data-testid="gok-phone-input"
              />
            </div>
            
            <div>
              <Label htmlFor="gok-password">{t('officialsManagement.password')}</Label>
              <Input
                id="gok-password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Min 6 characters"
                className="mt-1"
                data-testid="gok-password-input"
              />
            </div>
            
            <div>
              <Label htmlFor="gok-designation">{t('gok.gokDesignation')}</Label>
              <Select 
                value={formData.gok_designation} 
                onValueChange={(value) => setFormData({ ...formData, gok_designation: value })}
              >
                <SelectTrigger className="mt-1" data-testid="gok-designation-select">
                  <SelectValue placeholder={t('gok.selectGOKDesignation')} />
                </SelectTrigger>
                <SelectContent>
                  {GOK_DESIGNATIONS.map((designation) => (
                    <SelectItem key={designation.value} value={designation.value}>
                      {t(designation.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowCreateDialog(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button 
                type="submit" 
                disabled={createLoading}
                className="bg-amber-700 hover:bg-amber-800 text-white"
                data-testid="submit-gok-official"
              >
                {createLoading ? t('common.processing') : t('officialsManagement.create')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
